/**
 * 14b New lesson · 14c Import material · 14d Add an engagement · 14e Live.
 *
 * The four remaining spine screens, following `design/screens/14b|c|d|e.html`.
 * 14e is deliberately sparse: three controls and a big screen, everything else under More.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, subscribeToEvent } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useAction, useQuery } from '../../lib/useQuery';
import { Btn, Chip, DesignShell, Empty, ErrorNote, Loading, S, formatMinutes } from '../../design/kit';

/* ====================================================== 14b — new lesson */

interface Drafted {
  lesson: { id: string; title: string; objective: string };
  engagements: { id: string; title: string; type: string; questionIds: string[]; config: Record<string, unknown> }[];
  rationale: string;
}

const STEERS = ['Make it shorter', 'More group work', 'Harder', 'Add a diagram'];

export function NewLesson() {
  const { school } = useSession();
  const navigate = useNavigate();
  const cohorts = useQuery<{ cohorts: { id: string; name: string; grade: string }[] }>('/people/cohorts');
  const plans = useQuery<{ plans: { id: string; subject: string; grade: string; cohortIds: string[] }[] }>('/plans');

  const [cohortId, setCohortId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [topicName, setTopicName] = useState('');
  const [steer, setSteer] = useState('');
  const [draft, setDraft] = useState<Drafted | null>(null);

  useEffect(() => {
    if (!cohortId && cohorts.data?.cohorts[0]) setCohortId(cohorts.data.cohorts[0].id);
  }, [cohorts.data, cohortId]);

  const plan = useMemo(() => plans.data?.plans.find((p) => p.cohortIds.includes(cohortId)), [plans.data, cohortId]);
  const planDetail = useQuery<{ units: { id: string; name: string }[] }>(plan ? `/plans/${plan.id}` : null, [plan?.id]);
  const unitId = planDetail.data?.units[0]?.id;
  const unit = useQuery<{ topics: { id: string; name: string }[] }>(unitId ? `/units/${unitId}` : null, [unitId]);

  useEffect(() => {
    const first = unit.data?.topics?.[0];
    if (first && !topicId) setTopicId(first.id);
  }, [unit.data, topicId]);

  const drafting = useAction(async (withSteer?: string) => {
    const res = await api.post<Drafted>('/lessons/draft', {
      cohortId,
      topicId: topicId || undefined,
      topicName: topicId ? undefined : topicName || undefined,
      steer: withSteer ?? steer ?? undefined,
      durationMinutes: 60,
    });
    setDraft(res);
    setSteer('');
  });

  const minutes = (e: Drafted['engagements'][number]) => Number((e.config as { minutes?: number }).minutes ?? 0);
  const total = draft?.engagements.reduce((a, e) => a + minutes(e), 0) ?? 0;
  const cohortName = cohorts.data?.cohorts.find((c) => c.id === cohortId)?.name ?? '';

  return (
    <DesignShell
      brand={school?.name}
      title={
        <span>
          New lesson
          {draft ? (
            <span style={{ ...S.mutedXs, fontWeight: 500, marginLeft: '12px' }}>
              Drafted in seconds · nothing saved until you say so
            </span>
          ) : null}
        </span>
      }
    >
      {/* Teaching — what and for whom. */}
      <div style={{ ...S.card, display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'grid', gap: '6px', minWidth: '220px', flex: 1 }}>
          <span style={{ fontSize: '0.7812rem', fontWeight: 600, color: 'var(--c-ink-2)' }}>Teaching</span>
          <select
            value={cohortId}
            onChange={(e) => { setCohortId(e.target.value); setDraft(null); }}
            style={{ border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '10px 13px', fontSize: '0.8125rem', background: '#fff' }}
          >
            {cohorts.data?.cohorts.map((c) => <option key={c.id} value={c.id}>{c.grade} · {c.name}</option>)}
          </select>
        </label>

        {plan ? (
          <label style={{ display: 'grid', gap: '6px', minWidth: '240px', flex: 1 }}>
            <span style={{ fontSize: '0.7812rem', fontWeight: 600, color: 'var(--c-ink-2)' }}>
              Topic <span style={{ ...S.mutedXs, fontWeight: 500 }}>next in your plan</span>
            </span>
            <select
              value={topicId}
              onChange={(e) => { setTopicId(e.target.value); setDraft(null); }}
              style={{ border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '10px 13px', fontSize: '0.8125rem', background: '#fff' }}
            >
              {unit.data?.topics?.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
        ) : (
          <label style={{ display: 'grid', gap: '6px', minWidth: '240px', flex: 1 }}>
            <span style={{ fontSize: '0.7812rem', fontWeight: 600, color: 'var(--c-ink-2)' }}>What are you teaching?</span>
            <input
              value={topicName}
              onChange={(e) => setTopicName(e.target.value)}
              placeholder="Ratio in word problems"
              style={{ border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '10px 13px', fontSize: '0.8125rem' }}
            />
          </label>
        )}

        {!draft && (
          <Btn kind="primaryLg" onClick={() => void drafting.run()} disabled={drafting.pending || !cohortId || (!topicId && !topicName)}>
            {drafting.pending ? 'Drafting the hour…' : '✦ Draft the hour'}
          </Btn>
        )}
      </div>
      {drafting.error ? <ErrorNote error={drafting.error} /> : null}

      {draft && (
        <>
          <div style={S.card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.1875rem', fontWeight: 600, letterSpacing: '-.01em' }}>{draft.lesson.title}</span>
              <Chip tone="brand">✦ drafted</Chip>
            </div>
            <div style={S.sub}>
              {draft.engagements.length} engagements · {formatMinutes(total)} of checkpoints · {draft.lesson.objective}
            </div>

            <div style={{ display: 'grid', gap: '2px', marginTop: '16px' }}>
              {draft.engagements.map((e, i) => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '13px', padding: '13px 0', borderTop: i === 0 ? 'none' : '1px solid var(--c-line-soft)' }}>
                  <span style={{ width: '22px', flex: 'none', ...S.mutedSm, fontWeight: 600 }}>{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{e.title}</div>
                    <div style={S.sub}>
                      {String((e.config as { bloom?: string }).bloom || e.type.replace(/_/g, ' '))} ·{' '}
                      {e.questionIds.length} question{e.questionIds.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <span style={{ ...S.mutedSm, width: '58px', textAlign: 'right' }}>{minutes(e)} min</span>
                  <button
                    type="button"
                    onClick={() => navigate(`/teacher/engagements/${e.id}/questions`)}
                    style={{ ...S.link, border: 'none', background: 'none' }}
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '9px', marginTop: '18px', flexWrap: 'wrap' }}>
              <Btn kind="primaryLg" to={`/teacher/lessons/${draft.lesson.id}/publish`}>Save and schedule</Btn>
              <Btn kind="secondaryLg" onClick={() => void drafting.run()} disabled={drafting.pending}>✦ Draft it differently</Btn>
              <Btn kind="secondaryLg" to={`/teacher/lessons/${draft.lesson.id}/wizard/frame`}>Shape it myself</Btn>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' }}>
            <div style={S.card}>
              <div style={S.sectionLabel}>Why this shape</div>
              <p style={{ fontSize: '0.8125rem', lineHeight: 1.65, margin: '8px 0 0', color: 'var(--c-ink-2)' }}>{draft.rationale}</p>
            </div>
            <div style={S.card}>
              <div style={S.sectionLabel}>Change anything</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '10px 0' }}>
                {STEERS.map((s) => (
                  <Btn key={s} kind="secondarySm" onClick={() => void drafting.run(s)} disabled={drafting.pending}>{s}</Btn>
                ))}
              </div>
              <textarea
                value={steer}
                onChange={(e) => setSteer(e.target.value)}
                placeholder="Or type what you want changed."
                aria-label="Change anything"
                style={{ width: '100%', minHeight: '70px', border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '10px 13px', fontSize: '0.8125rem', resize: 'vertical' }}
              />
              <Btn kind="secondarySm" style={{ marginTop: '8px' }} onClick={() => void drafting.run()} disabled={drafting.pending || !steer.trim()}>
                Redraft with that
              </Btn>
            </div>
          </div>

          <p style={{ ...S.mutedSm, lineHeight: 1.65, maxWidth: '760px' }}>
            The five-step wizard is still here under <strong style={{ color: 'var(--c-ink-2)' }}>Shape it myself</strong> — thinking
            routine, access supports, closing check. Most lessons will not need it.
          </p>
        </>
      )}
      <span style={{ display: 'none' }}>{cohortName}</span>
    </DesignShell>
  );
}

/* ================================================== 14c — import material */

export function ImportMaterial() {
  const { school } = useSession();
  const navigate = useNavigate();
  const cohorts = useQuery<{ cohorts: { id: string; name: string; grade: string }[] }>('/people/cohorts');
  const [cohortId, setCohortId] = useState('');
  const [text, setText] = useState('');
  const [filename, setFilename] = useState('');

  useEffect(() => {
    if (!cohortId && cohorts.data?.cohorts[0]) setCohortId(cohorts.data.cohorts[0].id);
  }, [cohorts.data, cohortId]);

  const run = useAction(async () => {
    const res = await api.post<Drafted>('/lessons/import', { text, cohortId, filename: filename || undefined });
    navigate(`/teacher/lessons/${res.lesson.id}`);
  });

  const questionsFound = text.split(/\n/).filter((l) => /^(\d+[.)]|[-*•]|Q\d)/i.test(l.trim()) || l.trim().endsWith('?')).length;

  return (
    <DesignShell brand={school?.name} title="Import material">
      <div style={S.card}>
        <div style={{ fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-.01em' }}>
          Drop a worksheet, a deck or a past paper
        </div>
        <p style={{ ...S.mutedSm, lineHeight: 1.65, margin: '6px 0 16px', maxWidth: '640px' }}>
          SchoolVerse reads it, works out which topics it covers, and turns the questions into engagements you can run.
          Paste the text, or choose a plain-text or CSV file.
        </p>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '14px' }}>
          <label style={{ display: 'grid', gap: '6px', minWidth: '220px' }}>
            <span style={{ fontSize: '0.7812rem', fontWeight: 600, color: 'var(--c-ink-2)' }}>Which class?</span>
            <select
              value={cohortId}
              onChange={(e) => setCohortId(e.target.value)}
              style={{ border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '10px 13px', fontSize: '0.8125rem', background: '#fff' }}
            >
              {cohorts.data?.cohorts.map((c) => <option key={c.id} value={c.id}>{c.grade} · {c.name}</option>)}
            </select>
          </label>
          <label style={{ ...S.mutedSm, cursor: 'pointer' }}>
            <span style={{ display: 'inline-block', border: '1px solid var(--c-line-strong)', background: '#fff', fontSize: '0.8125rem', fontWeight: 500, padding: '10px 14px', borderRadius: '9px', color: 'var(--c-ink)' }}>
              Choose a file
            </span>
            <input
              type="file"
              accept=".txt,.md,.csv"
              style={{ position: 'absolute', width: 1, height: 1, opacity: 0 }}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setFilename(f.name);
                setText(await f.text());
              }}
            />
          </label>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Paste the material"
          placeholder={'Ratio worksheet\n1. Simplify the ratio 12:18.\n2. A recipe uses flour and sugar in the ratio 3:2…'}
          style={{ width: '100%', minHeight: '220px', border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '12px 14px', fontSize: '0.7812rem', fontFamily: 'ui-monospace,Menlo,monospace', lineHeight: 1.6, resize: 'vertical' }}
        />

        {text.trim().length > 20 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', marginTop: '12px' }}>
            <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--c-good-wash)', color: 'var(--c-good)', fontSize: '0.6875rem', fontWeight: 600, display: 'grid', placeItems: 'center' }}>✓</span>
            <div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{filename || 'Pasted material'}</div>
              <div style={S.sub}>{questionsFound} question{questionsFound === 1 ? '' : 's'} found</div>
            </div>
          </div>
        )}

        {run.error ? <ErrorNote error={run.error} /> : null}
        <div style={{ display: 'flex', gap: '9px', marginTop: '18px', flexWrap: 'wrap' }}>
          <Btn kind="primaryLg" onClick={() => void run.run()} disabled={run.pending || text.trim().length < 20 || !cohortId}>
            {run.pending ? 'Reading it…' : 'Make a lesson from these'}
          </Btn>
        </div>
      </div>

      <div style={{ ...S.card, background: 'var(--c-brand-wash)', border: '1px solid var(--c-brand-line)' }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>Nothing is thrown away.</div>
        <p style={{ fontSize: '0.7812rem', lineHeight: 1.65, margin: '6px 0 0', color: 'var(--c-brand-deep)' }}>
          Your questions are kept as written — SchoolVerse groups them, it does not rewrite what already works. Your material
          is used to draft your lessons and nothing else. It is not shared with other schools and not used to train anything.
        </p>
      </div>
    </DesignShell>
  );
}

/* ================================================ 14d — add an engagement */

interface Move {
  type: string; label: string; blurb: string; participation: string;
  answerShape: string; marking: string; primary: boolean; allowedContexts?: string[]; allowed?: string[];
}

export function AddEngagement() {
  const { id = '' } = useParams();
  const { school } = useSession();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const context = search.get('context') ?? 'live';
  const catalogue = useQuery<{ primary: Move[]; more: Move[] }>('/lessons/moves/catalogue');
  const lesson = useQuery<{ lesson: { title: string }; cohorts: { name: string }[] }>(`/lessons/${id}`);
  const [more, setMore] = useState(false);
  const [picked, setPicked] = useState<Move | null>(null);
  const [count, setCount] = useState(1);

  const add = useAction(async () => {
    if (!picked) return;
    await api.post(`/lessons/${id}/engagements`, { type: picked.type, questionCount: count, context });
    navigate(`/teacher/lessons/${id}`);
  });

  const list = more ? catalogue.data?.more ?? [] : catalogue.data?.primary ?? [];

  return (
    <DesignShell
      brand={school?.name}
      title={
        <span>
          Add an engagement
          {lesson.data ? (
            <span style={{ ...S.mutedXs, fontWeight: 500, marginLeft: '12px' }}>
              {lesson.data.lesson.title} · {lesson.data.cohorts.map((c) => c.name).join(', ')}
            </span>
          ) : null}
        </span>
      }
      actions={<Btn kind="secondary" onClick={() => setMore((v) => !v)}>{more ? '← The six moves' : 'Something else'}</Btn>}
    >
      {catalogue.loading ? (
        <Loading />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' }}>
            {list.map((m) => {
              const allowed = (m.allowedContexts ?? m.allowed ?? []).includes(context);
              const on = picked?.type === m.type;
              return (
                <button
                  key={m.type}
                  type="button"
                  aria-pressed={on}
                  disabled={!allowed}
                  onClick={() => setPicked(m)}
                  style={{
                    textAlign: 'left', cursor: allowed ? 'pointer' : 'not-allowed', opacity: allowed ? 1 : 0.5,
                    background: '#fff', borderRadius: '13px', padding: '20px 22px',
                    border: on ? '1.5px solid var(--c-brand)' : '1px solid var(--c-line)',
                    boxShadow: on ? '0 0 0 4px rgba(27,58,99,.08)' : 'none',
                  }}
                >
                  <div style={{ fontSize: '0.9062rem', fontWeight: 600 }}>{m.label}</div>
                  <div style={{ ...S.mutedSm, marginTop: '4px' }}>{m.blurb}</div>
                  <div style={{ ...S.mutedXs, marginTop: '10px', lineHeight: 1.6 }}>
                    {m.participation.replace(/_/g, ' ')}
                    <br />
                    {m.answerShape}
                  </div>
                  {!allowed && (
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-warn-ink)', marginTop: '8px' }}>
                      Not available in this context
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {picked && (
            <div style={{ ...S.card, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
              {picked.marking !== 'none' && (
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontSize: '0.7812rem', fontWeight: 600, color: 'var(--c-ink-2)' }}>Questions</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={count}
                    onChange={(e) => setCount(Number(e.target.value))}
                    style={{ width: '90px', border: '1px solid rgba(17,20,28,.12)', borderRadius: '9px', padding: '10px 13px', fontSize: '0.8125rem' }}
                  />
                </label>
              )}
              <Btn kind="primaryLg" onClick={() => void add.run()} disabled={add.pending}>
                {add.pending ? 'Writing it…' : `Add · ${picked.label}`}
              </Btn>
            </div>
          )}
          {add.error ? <ErrorNote error={add.error} /> : null}

          <p style={{ ...S.mutedSm, lineHeight: 1.65, maxWidth: '760px' }}>
            Each move is a set of defaults for who takes part, what they do and how they answer. Once it is in the
            lesson you can change any of those, and everything the old picker offered — pairs, stations, peer review,
            choice boards, question sets — is in <strong style={{ color: 'var(--c-ink-2)' }}>Something else</strong>.
          </p>
        </>
      )}
    </DesignShell>
  );
}

/* ============================================================= 14e — live */

interface LiveState {
  event: { id: string; title: string; status: string; joinCode?: string; revealed: boolean; extraSeconds: number; currentBlockIndex: number };
  revision: number;
  engagementCount: number;
  current: { id: string; title: string; questions: { id: string; stem: string; options: string[]; answer: string[] | null }[] } | null;
  participants: { id: string; name: string; status: string }[];
  answered?: number;
}

export function LiveRunner() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useQuery<LiveState>(`/events/${id}/state`);
  const [showMore, setShowMore] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);

  useEffect(() => {
    if (!id) return;
    let seen = -1;
    return subscribeToEvent(id, {
      onRevision: (revision) => { if (revision > seen) { seen = revision; reload(); } },
      onEvent: (type) => { if (type === 'response_received') reload(); },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const act = useAction(async (path: string, body?: unknown) => {
    await api.post(`/events/${id}/${path}`, body);
    reload();
  });

  useEffect(() => {
    if (data?.event.status === 'lobby') navigate(`/teacher/events/${id}/lobby`, { replace: true });
    if (data?.event.status === 'ended') navigate(`/teacher/events/${id}/ended`, { replace: true });
  }, [data?.event.status, id, navigate]);

  if (loading && !data) return <div style={{ minHeight: '100vh', background: 'var(--c-dark)' }}><Loading /></div>;
  if (error || !data) return <div style={{ padding: 40 }}><ErrorNote error={error} retry={reload} /></div>;

  const present = data.participants.filter((p) => p.status !== 'left').length;
  const question = data.current?.questions[0];
  const last = data.event.currentBlockIndex + 1 >= data.engagementCount;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--c-dark)', color: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* One bar: what is running, where to join, and More. Nothing else competes. */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', padding: '18px 30px', borderBottom: '1px solid rgba(255,255,255,.1)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
          <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-good)', background: 'var(--c-good-wash)', padding: '5px 9px', borderRadius: '5px' }}>Live</span>
          <span style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{data.event.title}</span>
          <span style={{ fontSize: '0.7812rem', color: 'var(--c-ink-on-dark)' }}>
            {data.event.currentBlockIndex + 1} of {data.engagementCount}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {data.event.joinCode && (
            <span style={{ fontSize: '0.7812rem', color: 'var(--c-ink-on-dark)' }}>
              Join at schoolverse.pedagogyy.com · code{' '}
              <strong style={{ color: '#fff', fontFamily: 'ui-monospace,Menlo,monospace', letterSpacing: '.14em' }}>
                {data.event.joinCode}
              </strong>
            </span>
          )}
          <Btn kind="secondary" style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.22)' }} onClick={() => setShowMore(true)}>
            More
          </Btn>
        </div>
      </header>

      <div style={{ flex: 1, padding: '44px 30px', display: 'flex', flexDirection: 'column', gap: '30px', maxWidth: '1100px', margin: '0 auto', width: '100%' }}>
        <div style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--c-ink-on-dark)' }}>
          On the projector
        </div>
        <h2 style={{ fontSize: 'clamp(24px, 3.4vw, 40px)', fontWeight: 600, letterSpacing: '-.02em', lineHeight: 1.2, margin: 0 }}>
          {question?.stem ?? data.current?.title ?? 'Nothing on screen'}
        </h2>

        {question && question.options.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
            {question.options.map((o, i) => {
              const right = data.event.revealed && question.answer?.includes(String(i));
              return (
                <div
                  key={o + i}
                  style={{
                    fontSize: '1.1875rem', padding: '18px 20px', borderRadius: '12px',
                    background: right ? 'rgba(31,143,78,.2)' : 'rgba(255,255,255,.06)',
                    border: right ? '1px solid var(--c-good)' : '1px solid rgba(255,255,255,.12)',
                  }}
                >
                  <span style={{ opacity: 0.5, marginRight: '12px' }}>{String.fromCharCode(65 + i)}</span>
                  {o}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '2.125rem', fontWeight: 600, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums' }}>
            {data.answered ?? 0}
            <span style={{ fontSize: '1.1875rem', color: 'var(--c-ink-on-dark)' }}> / {present}</span>
          </span>
          <div style={{ flex: 1, minWidth: '200px', height: '8px', background: 'rgba(255,255,255,.1)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${present ? ((data.answered ?? 0) / present) * 100 : 0}%`, background: 'var(--c-good)' }} />
          </div>
          {data.event.extraSeconds > 0 && (
            <span style={{ fontSize: '0.7812rem', color: 'var(--c-ink-on-dark)' }}>+{data.event.extraSeconds}s added</span>
          )}
        </div>
      </div>

      {/* The three controls. */}
      <footer style={{ display: 'flex', gap: '12px', padding: '20px 30px', borderTop: '1px solid rgba(255,255,255,.1)', justifyContent: 'center', flexWrap: 'wrap' }}>
        <Btn
          kind="secondaryLg"
          style={{ background: data.event.revealed ? '#fff' : 'transparent', color: data.event.revealed ? 'var(--c-ink)' : '#fff', border: '1px solid rgba(255,255,255,.22)', fontSize: '0.875rem', padding: '14px 22px' }}
          onClick={() => void act.run('reveal', { revealed: !data.event.revealed })}
        >
          {data.event.revealed ? 'Hide the answer' : 'Reveal the answer'}
        </Btn>
        <Btn
          kind="secondaryLg"
          style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.22)', fontSize: '0.875rem', padding: '14px 22px' }}
          onClick={() => void act.run('more-time', { seconds: 120 })}
        >
          +2 min
        </Btn>
        <Btn
          kind="primary"
          style={{ fontSize: '0.875rem', padding: '14px 22px' }}
          onClick={() => (last ? setConfirmEnd(true) : void act.run('next'))}
        >
          {last ? 'Finish →' : 'Next →'}
        </Btn>
      </footer>

      <p style={{ ...S.mutedXs, textAlign: 'center', padding: '0 30px 22px', margin: 0, color: 'var(--c-ink-on-dark)' }}>
        Student names, the response list and hands raised are under <strong style={{ color: 'var(--c-ink-faint)' }}>More</strong>.
        Nothing else competes with the room.
      </p>

      {showMore && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,20,28,.6)', display: 'grid', placeItems: 'center', padding: '20px', zIndex: 100 }} onMouseDown={(e) => e.target === e.currentTarget && setShowMore(false)}>
          <div role="dialog" aria-modal="true" aria-label="More" style={{ background: '#fff', color: 'var(--c-ink)', borderRadius: '14px', padding: '24px', width: 'min(520px, 100%)', display: 'grid', gap: '9px' }}>
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '4px' }}>More</div>
            <div style={{ ...S.mutedSm, marginBottom: '6px' }}>{present} in the room</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {data.participants.map((p) => <Chip key={p.id} tone="plain">{p.name}</Chip>)}
              {data.participants.length === 0 && <span style={S.mutedSm}>Nobody has joined yet.</span>}
            </div>
            <Btn kind="secondary" onClick={() => { void act.run('pause'); setShowMore(false); }}>
              {data.event.status === 'paused' ? 'Resume the room' : 'Pause the room'}
            </Btn>
            <Btn kind="secondary" to={`/teacher/events/${id}/projector`}>Projector view</Btn>
            <Btn kind="secondary" to={`/teacher/events/${id}/groups`}>Groups</Btn>
            <Btn kind="secondary" to={`/teacher/events/${id}/stations`}>Stations</Btn>
            <Btn kind="secondary" to={`/teacher/events/${id}/challenge`}>Team challenge</Btn>
            <Btn kind="danger" onClick={() => { setShowMore(false); setConfirmEnd(true); }}>End the event early</Btn>
            <Btn kind="secondary" onClick={() => setShowMore(false)}>Close</Btn>
          </div>
        </div>
      )}

      {confirmEnd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,20,28,.6)', display: 'grid', placeItems: 'center', padding: '20px', zIndex: 100 }} onMouseDown={(e) => e.target === e.currentTarget && setConfirmEnd(false)}>
          <div role="dialog" aria-modal="true" aria-label="End this event?" style={{ background: '#fff', color: 'var(--c-ink)', borderRadius: '14px', padding: '26px', width: 'min(520px, 100%)' }}>
            <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>End this event?</div>
            <p style={{ ...S.mutedSm, lineHeight: 1.65 }}>
              Everyone&rsquo;s answers are kept. Results are worked out now, but nothing reaches students until you
              release them.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', marginTop: '18px' }}>
              <Btn kind="secondary" onClick={() => setConfirmEnd(false)}>Keep going</Btn>
              <Btn kind="primary" onClick={async () => { await act.run('end'); navigate(`/teacher/events/${id}/ended`); }}>End it</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
